<section class="address-validation-prompt">
  <header class="prompt-header">
    <button
      *ngIf="viewMode === 'edit'"
      type="button"
      mat-icon-button
      class="back-button"
      [attr.aria-label]="'GoBack' | translate"
      (click)="backToConfirm()"
    >
      <mat-icon>arrow_back</mat-icon>
    </button>

    <div class="title-block" [class.with-back]="viewMode === 'edit'">
      <h2>
        {{
          (viewMode === 'confirm'
            ? 'AddressValidationCheckTitle'
            : 'AddressValidationModifyTitle') | translate
        }}
      </h2>
      <p>
        {{
          (viewMode === 'confirm'
            ? 'AddressValidationReviewDescription'
            : 'AddressValidationModifyDescription') | translate
        }}
      </p>
    </div>

    <button
      type="button"
      mat-icon-button
      class="close-button"
      [attr.aria-label]="'Close' | translate"
      (click)="cancel()"
    >
      <mat-icon>close</mat-icon>
    </button>
  </header>

  <ng-container *ngIf="viewMode === 'confirm'; else editTemplate">
    <div class="action-instruction" [class.retry-limit-reached]="retryLimitReached">
      <mat-icon>{{ retryLimitReached ? 'warning' : 'location_on' }}</mat-icon>
      <p>{{ (retryLimitReached ? 'AddressValidationRetryLimitReached' : actionInstructionKey) | translate }}</p>
    </div>

    <div class="address-compare">
      <article class="address-panel">
        <div class="panel-header">
          <span class="panel-label">{{ "AddressValidationYouEntered" | translate }}</span>
          <button
            *ngIf="canSpeakAddress()"
            mat-icon-button
            type="button"
            [attr.aria-label]="((enteredAddressSpeaking$ | async) ? 'StopSpeaking' : 'SpeakQuestion') | translate"
            (click)="toggleSpeakAddress()"
            [matTooltip]="((enteredAddressSpeaking$ | async) ? 'StopSpeaking' : 'SpeakQuestion') | translate"
            class="speak-btn"
          >
            <mat-icon>{{ (enteredAddressSpeaking$ | async) ? 'stop' : 'volume_up' }}</mat-icon>
          </button>
        </div>
        <p>{{ enteredAddressText || ("AddressValidationNoAddressProvided" | translate) }}</p>
      </article>
    </div>

    <footer class="prompt-actions">
      <button
        *ngIf="canConfirmAddress && !retryLimitReached"
        type="button"
        mat-stroked-button
        class="btn-outline-content"
        (click)="switchToEdit()"
      >
        <mat-icon>edit</mat-icon>
        {{ "AddressValidationModifyAddress" | translate }}
      </button>
      <button
        *ngIf="!canConfirmAddress"
        type="button"
        mat-raised-button
        class="btn-lg btn-dark"
        (click)="switchToEdit()"
      >
        <mat-icon>edit</mat-icon>
        {{ "AddressValidationModifyAddress" | translate }}
      </button>
      <button
        *ngIf="canConfirmAddress"
        type="button"
        mat-raised-button
        class="btn-lg btn-dark"
        (click)="confirmOverride()"
      >
        <mat-icon>check</mat-icon>
        {{ "AddressValidationThisAddressCorrect" | translate }}
      </button>
    </footer>
  </ng-container>

  <ng-template #editTemplate>
    <form class="address-form" [formGroup]="form" (ngSubmit)="submitModifiedAddress()" (keydown.enter)="$event.preventDefault()">
      <mat-form-field appearance="fill">
        <mat-label>{{ "Postal code" | translate }}</mat-label>
        <input
          matInput
          formControlName="postalCode"
          [mask]="isBrazilCountry ? '00000-000' : null"
          [dropSpecialCharacters]="false"
          [attr.inputmode]="isBrazilCountry ? 'numeric' : null"
          [attr.maxlength]="isBrazilCountry ? 9 : null"
        />
        <mat-spinner *ngIf="cepLoading" matSuffix [diameter]="18"></mat-spinner>
        <mat-error *ngIf="form.controls.postalCode.hasError('required')">
          {{ "Postal code" | translate }} {{ "is required" | translate }}.
        </mat-error>
        <mat-error *ngIf="form.controls.postalCode.hasError('brazilCep')">
          {{ "AddressPostalCodeInvalidDigits" | translate }}
        </mat-error>
      </mat-form-field>

      <mat-form-field appearance="fill">
        <mat-label>{{ "Street name" | translate }}</mat-label>
        <input matInput formControlName="streetName" />
        <mat-error>
          {{ "Street name" | translate }} {{ "is required" | translate }}.
        </mat-error>
      </mat-form-field>

      <mat-form-field appearance="fill">
        <mat-label>{{ "Street number" | translate }}</mat-label>
        <input matInput formControlName="streetNumber" />
        <mat-error>
          {{ "Street number" | translate }} {{ "is required" | translate }}.
        </mat-error>
      </mat-form-field>

      <mat-form-field appearance="fill">
        <mat-label>{{ "Unit / Apartment Number" | translate }}</mat-label>
        <input matInput formControlName="unitNumber" />
      </mat-form-field>

      <mat-form-field appearance="fill">
        <mat-label>{{ "Building Name" | translate }}</mat-label>
        <input matInput formControlName="buildingName" />
      </mat-form-field>

      <mat-form-field appearance="fill">
        <mat-label>{{ "Reference Point" | translate }}</mat-label>
        <input matInput formControlName="referencePoint" />
      </mat-form-field>

      <mat-form-field appearance="fill">
        <mat-label>{{ "Neighborhood" | translate }}</mat-label>
        <input matInput formControlName="neighborhood" />
      </mat-form-field>

      <mat-form-field appearance="fill">
        <mat-label>{{ "City" | translate }}</mat-label>
        <input matInput formControlName="city" />
        <mat-error>
          {{ "City" | translate }} {{ "is required" | translate }}.
        </mat-error>
      </mat-form-field>

      <mat-form-field appearance="fill" *ngIf="isBrazilCountry; else stateInput">
        <mat-label>{{ "State" | translate }}</mat-label>
        <mat-select formControlName="state">
          <mat-option *ngFor="let brState of brazilStates" [value]="brState.code">
            {{ brState.name }} ({{ brState.code }})
          </mat-option>
        </mat-select>
        <mat-error>
          {{ "State" | translate }} {{ "is required" | translate }}.
        </mat-error>
      </mat-form-field>
      <ng-template #stateInput>
        <mat-form-field appearance="fill">
          <mat-label>{{ "State" | translate }}</mat-label>
          <input matInput formControlName="state" />
          <mat-error>
            {{ "State" | translate }} {{ "is required" | translate }}.
          </mat-error>
        </mat-form-field>
      </ng-template>

      <mat-form-field appearance="fill">
        <mat-label>{{ "Country" | translate }}</mat-label>
        <mat-select formControlName="country">
          <mat-option *ngFor="let item of countries" [value]="item.name">
            {{ item.name }}
          </mat-option>
        </mat-select>
        <mat-error>
          {{ "Country" | translate }} {{ "is required" | translate }}.
        </mat-error>
      </mat-form-field>

      <footer class="prompt-actions">
        <button
          type="button"
          mat-stroked-button
          class="btn-outline-content"
          (click)="backToConfirm()"
        >
          {{ "Cancel" | translate }}
        </button>
        <button type="submit" mat-raised-button class="btn-lg btn-dark">
          {{ "AddressValidationSaveAddress" | translate }}
        </button>
      </footer>
    </form>
  </ng-template>
</section>
